<script setup lang="ts">
import { defineProps, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import useStickyAlerts from '@/modules/alerts/composables/useStickyAlerts';
import { MessageStickyAlertPropsSchema } from '@/modules/alerts/models/message-sticky-alert-props';
import { STICKY_ALERT_TYPE } from '@/modules/alerts/models/sticky-alert-type';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const router = useRouter();
const { findAlert, answerAlert, setupTimeout } = useStickyAlerts();

const props = defineProps<MessageStickyAlertPropsSchema>();

const alert = findAlert(STICKY_ALERT_TYPE.MESSAGE, props.id);

const close = () => {
  answerAlert(alert, { result: 'DISMISS' });
};

const handleSegmentClick = (link?: string) => {
  if (link) {
    close();
    router.push(link);
  }
};

setupTimeout(STICKY_ALERT_TYPE.MESSAGE, props.id, alert, props.options?.timeoutMs);

if (props.options?.dismissCondition) {
  const shouldDismiss = computed(() => props.options.dismissCondition());

  watch(
    shouldDismiss,
    shouldDismissValue => {
      if (shouldDismissValue && alert) {
        close();
      }
    },
    { immediate: true },
  );
}
</script>

<template>
  <div class="accented-container">
    <div class="accent"></div>
    <div class="message-alert">
      <div class="alert-content d-flex justify-content-between align-items-center">
        <span class="flex-grow-1 text-center" data-testid="message-alert-content">
          <template v-for="(segment, index) in props.messageSegments" :key="index">
            <span
              v-if="segment.link"
              class="link-segment underline-link-from-center"
              @click="handleSegmentClick(segment.link)"
            >
              {{ segment.text }}
            </span>
            <span v-else>
              {{ segment.text }}
            </span>
          </template>
        </span>
        <span
          class="cursor-pointer ml-2"
          @click.stop="close"
          data-testid="notification-alert-close-button"
        >
          <font-awesome-icon icon="xmark" size="lg" class="close-icon" />
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '~@/assets/styles/_tools.scss';
@import '~@/assets/styles/_settings.scss';

$border-radius: 0.5rem;

.accented-container {
  position: relative;
  width: 100%;
}

.accent {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: $border-radius;
  border-top-left-radius: $border-radius;
  border-bottom-left-radius: $border-radius;
}

.message-alert {
  width: 100%;
  background-color: $body-bg;
  border-radius: $border-radius;
  margin-left: 0.25rem;
  border-style: solid;
  border-width: rem(1px);
  border-color: color-contrast($body-bg);
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.25);
}

.separator {
  height: rem(1px);
  background-color: color-contrast($body-bg);
  opacity: 0.1;
}

.alert-content {
  padding: 0.75rem;
}

.link-segment {
  color: $link-color;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.close-icon {
  transform: scale(1.3);
}
</style>
